import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useTransferOwnershipList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refTransferOwnershipListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'stringID',
      label: 'ID',
      sortable: true,
    },
    {
      key: 'fileNo',
      label: 'File Details',
      sortable: true,
    },
    {
      key: 'transferor',
      label: 'Transferor',
      sortable: false,
    },
    {
      key: 'transferee',
      label: 'Transferee',
      sortable: false,
    },
    { key: 'currentStatus', label: 'Status', sortable: true },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.toPerPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalRequests = ref(0)
  const searchQuery = ref(route.query.toSearch || '')
  const currentPage = ref(route.query.toPage ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const sortBy = ref(route.query.toSortBy ?? 'stringID')
  const isSortDirDesc = ref(route.query.toSortDesc !== 'false')
  const statusFilter = ref([])
  const fileTypeFilter = ref([])
  const fileStatusFilter = ref([])
  const requestData = ref([])
  if (Array.isArray(route.query.toStatus)) {
    statusFilter.value = route.query.toStatus
  } else if (route.query.toStatus) {
    statusFilter.value = [route.query.toStatus]
  }
  if (Array.isArray(route.query.toFileType)) {
    fileTypeFilter.value = route.query.toFileType
  } else if (route.query.toFileType) {
    fileTypeFilter.value = [route.query.toFileType]
  }
  if (Array.isArray(route.query.toFileStatus)) {
    fileStatusFilter.value = route.query.toFileStatus
  } else if (route.query.toFileStatus) {
    fileStatusFilter.value = [route.query.toFileStatus]
  }
  const toDeletedID = ref(null)
  let initialLoad = true
  const currentDataLength = ref(0)

  const dataMeta = computed(() => {
    const localItemsCount = refTransferOwnershipListTable.value ? refTransferOwnershipListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalRequests.value,
    }
  })

  const refetchData = () => {
    refTransferOwnershipListTable.value.refresh()
  }

  let timer = null
  watch([searchQuery, statusFilter, fileStatusFilter, fileTypeFilter], () => {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      currentPage.value = 1
      refetchData()
    }, 1000)
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchTransferOwnershipLists = (ctx, callback) => {
    store
      .dispatch('app-transfer-ownership-list/fetchTransferOwnershipLists', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        fileStatus: fileStatusFilter.value,
        fileType: fileTypeFilter.value,
      },
      { root: true })
      .then(response => {
        const { list, total } = response.data
        currentDataLength.value = list.length
        requestData.value = list
        callback(list)
        totalRequests.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              toSearch: searchQuery.value,
              toPerPage: perPage.value,
              toPage: currentPage.value,
              toSortBy: sortBy.value,
              toSortDesc: isSortDirDesc.value,
              toStatus: statusFilter.value,
              toFileStatus: fileStatusFilter.value,
              toFileType: fileTypeFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }
  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveLogStatusVariant = status => {
    if (status === 'Approved') return 'success'
    if (status === 'Awaiting Approval') return 'warning'
    if (status === 'Rejected by NV') return 'danger'
    if (status === 'Pending NP') return 'primary'
    if (status === 'Expired') return 'secondary'
    if (status === 'Withdrawn by NP') return 'secondary'
    if (status === 'Withdrawn by OP') return 'secondary'
    return 'primary'
  }

  return {
    fetchTransferOwnershipLists,
    tableColumns,
    perPage,
    currentPage,
    totalRequests,
    dataMeta,
    perPageOptions,
    sortBy,
    isSortDirDesc,
    refTransferOwnershipListTable,
    requestData,

    searchQuery,
    statusFilter,
    fileStatusFilter,
    fileTypeFilter,

    resolveLogStatusVariant,
    refetchData,
    currentDataLength,

    toDeletedID,
  }
}
